import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ResourceColumn, ResourceListPage } from '../../components/resource/ResourceListPage';
import { OrdemManutencao, StatusOrdemManutencao } from '../../types/manutencao';
import { ListParams, operadoresService, ordensManutencaoService } from '../../services';

const columns: ResourceColumn<OrdemManutencao>[] = [
  { header: "Ordem", render: (item) => "OM " + item.id },
  { header: "Máquina", accessor: "maquina_nome" },
  { header: "Tipo", accessor: "tipo_manutencao" },
  { header: "Prioridade", accessor: "prioridade" },
  { header: "Status", accessor: "status" },
  { 
    header: "Finalizado", 
    render: (item) => item.finalizado ? "Sim" : "Não" 
  }
];

const statusOptions: Array<{ value: StatusOrdemManutencao; label: string }> = [
  { value: 'ABERTA', label: 'Aberta' },
  { value: 'EM_ANDAMENTO', label: 'Em Andamento' },
  { value: 'CONCLUIDA', label: 'Concluída' },
  { value: 'CANCELADA', label: 'Cancelada' }
];

export const OrdensManutencaoListPage: React.FC = () => {
  const navigate = useNavigate();
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [finalizado, setFinalizado] = useState('');
  const [status, setStatus] = useState('');
  const [responsavel, setResponsavel] = useState('');
  const [operadores, setOperadores] = useState<Array<{ id: number; nome: string }>>([]);
  const [loadingOperadores, setLoadingOperadores] = useState(false);

  useEffect(() => {
    const loadOperadores = async () => {
      setLoadingOperadores(true);
      try {
        const response = await operadoresService.list({ page: 1, page_size: 100 });
        setOperadores(response.results);
      } catch (err) {
        console.error('Erro ao carregar operadores:', err);
      } finally {
        setLoadingOperadores(false);
      }
    };
    loadOperadores();
  }, []);

  const list = useCallback(
    (params?: ListParams) =>
      ordensManutencaoService.list({
        ...params,
        finalizado: finalizado !== '' ? finalizado === 'true' : undefined,
        status: status || undefined,
        responsavel: responsavel || undefined,
        data_inicio: dataInicio || undefined,
        data_fim: dataFim || undefined,
      }),
    [dataFim, dataInicio, finalizado, status, responsavel],
  );
  const filteredService = useMemo(
    () => ({ ...ordensManutencaoService, list }),
    [list],
  );

  return (
    <>
      <Form className="d-flex flex-wrap gap-3 mb-3">
        <Form.Group>
          <Form.Label>Finalizado</Form.Label>
          <Form.Select
            value={finalizado}
            onChange={(event) => setFinalizado(event.target.value)}
          >
            <option value="">Todos</option>
            <option value="true">Sim</option>
            <option value="false">Não</option>
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label>Status</Form.Label>
          <Form.Select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">Todos</option>
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="flex-grow-1" style={{ minWidth: '240px' }}>
          <Form.Label>Responsável</Form.Label>
          <Form.Select
            value={responsavel}
            onChange={(event) => setResponsavel(event.target.value)}
            disabled={loadingOperadores}
          >
            <option value="">{loadingOperadores ? 'Carregando...' : 'Todos'}</option>
            {operadores.map((item) => (
              <option key={item.id} value={String(item.id)}>
                {item.nome}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label>Data de início</Form.Label>
          <Form.Control
            type="date"
            value={dataInicio}
            onChange={(event) => setDataInicio(event.target.value)}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Data de fim</Form.Label>
          <Form.Control
            type="date"
            value={dataFim}
            onChange={(event) => setDataFim(event.target.value)}
          />
        </Form.Group>
      </Form>
      <ResourceListPage
        title="Ordens de manutenção"
      description="Ordens emitidas para as máquinas."
        columns={columns}
        service={filteredService}
        createPath="/admin/manutencao/ordens-manutencao/novo"
        onEdit={(item) =>
          item.id &&
          navigate('/admin/manutencao/ordens-manutencao/editar', { state: { record: item } })
        }
      />
    </>
  );
};
